import {
    cents,
    dateDay,
    defineFilters,
    enumOne,
    flag,
    multiEnum,
    objectId,
    text,
    type FilterOption
} from '@/components/kit/filters'
import { DepositStatus, PaymentMethod, type DepositSortField } from '@/types/payment'

export const DEPOSIT_STATUS_OPTIONS: FilterOption<DepositStatus>[] = [
    { value: DepositStatus.INITIATED, label: 'Initiated', hint: 'Checkout opened, nothing paid yet' },
    { value: DepositStatus.PENDING, label: 'Pending', hint: 'Awaiting confirmation from the processor' },
    { value: DepositStatus.COMPLETED, label: 'Completed', hint: 'Paid, not yet credited to the wallet' },
    { value: DepositStatus.CREDITED, label: 'Credited', hint: 'Balance is on the account' },
    { value: DepositStatus.FAILED, label: 'Failed' },
    { value: DepositStatus.CANCELED, label: 'Canceled' },
    { value: DepositStatus.UNKNOWN, label: 'Unknown', hint: 'The processor reported a state we do not map' }
]

export const PAYMENT_METHOD_OPTIONS: FilterOption<PaymentMethod>[] = [
    { value: PaymentMethod.Stripe, label: 'Stripe' },
    { value: PaymentMethod.CryptoChill, label: 'Crypto' }
]

export const DEPOSIT_SORT_OPTIONS: FilterOption<DepositSortField>[] = [
    { value: 'createdAt', label: 'Created' },
    { value: 'amount', label: 'Amount' },
    { value: 'status', label: 'Status' },
    { value: 'method', label: 'Method' }
]

const ORDER_OPTIONS: FilterOption<'asc' | 'desc'>[] = [
    { value: 'desc', label: 'Descending' },
    { value: 'asc', label: 'Ascending' }
]

/**
 * `dateTo` goes out as the start of its day, unlike the rest of the app. The endpoint
 * pushes the upper bound to end-of-day itself (`setUTCHours(23, 59, 59, 999)`), so
 * either spelling selects the same range; this one matches what the legacy page sent.
 */
export const depositFilters = defineFilters(
    {
        search: text({ label: 'Search' }),
        user: objectId({ label: 'User' }),
        methods: multiEnum(PAYMENT_METHOD_OPTIONS, { label: 'Method' }),
        statuses: multiEnum(DEPOSIT_STATUS_OPTIONS, { label: 'Status' }),
        dateFrom: dateDay({ label: 'From', boundary: 'start' }),
        dateTo: dateDay({ label: 'To', boundary: 'start' }),
        amountMin: cents({ label: 'Min amount' }),
        amountMax: cents({ label: 'Max amount' }),
        sortField: enumOne(DEPOSIT_SORT_OPTIONS, 'createdAt', { label: 'Sort' }),
        sortDir: enumOne(ORDER_OPTIONS, 'desc', { label: 'Order' }),
        /** Server-computed totals across the whole filtered set, not just this page. */
        includeSummary: flag({ label: 'Totals' })
    },
    { defaultLimit: 25 }
)

export type DepositSearch = ReturnType<typeof depositFilters.parse>
